import React, { useState } from 'react';
import { useProject } from '../context/ProjectContext';

const FolderTree = ({ 
  folders, 
  projects, 
  currentProjectId,
  currentFolderId,
  onProjectSelect,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  onProjectMove,
  onProjectCreate,
  isPersonal = false 
}) => {
  return (
    <div className="space-y-1">
      {folders.map(folder => (
        <FolderNode
          key={folder.id}
          folder={folder}
          currentProjectId={currentProjectId}
          currentFolderId={currentFolderId}
          onProjectSelect={onProjectSelect}
          onFolderSelect={onFolderSelect}
          onFolderCreate={onFolderCreate}
          onFolderRename={onFolderRename}
          onFolderDelete={onFolderDelete}
          onFolderMove={onFolderMove}
          onProjectMove={onProjectMove}
          onProjectCreate={onProjectCreate}
          level={0}
        />
      ))}
      {projects && projects.length > 0 && (
        <div className="space-y-1">
          {projects.map(project => (
            <ProjectNode
              key={project.id}
              project={project}
              isActive={project.id === currentProjectId}
              onSelect={onProjectSelect}
              level={0}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const FolderNode = ({ 
  folder, 
  currentProjectId,
  currentFolderId,
  onProjectSelect,
  onFolderSelect,
  onFolderCreate,
  onFolderRename,
  onFolderDelete,
  onFolderMove,
  onProjectMove,
  onProjectCreate,
  level = 0 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showCreateSubfolderModal, setShowCreateSubfolderModal] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleCreateSubfolder = () => {
    setNewName('');
    setError('');
    setShowCreateSubfolderModal(true);
    closeContextMenu();
  };

  const handleRename = () => {
    setNewName(folder.name);
    setError('');
    setShowRenameModal(true);
    closeContextMenu();
  };

  const handleDelete = () => {
    closeContextMenu();
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await onFolderDelete(folder.id);
      setShowDeleteModal(false);
    } catch (err) {
      setError(err.message || 'Failed to delete folder');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setError('');
  };

  const handleConfirmRename = async () => {
    if (!newName || !newName.trim()) {
      setError('Folder name is required');
      return;
    }
    
    if (newName.trim() === folder.name) {
      setShowRenameModal(false);
      setNewName('');
      setError('');
      return;
    }

    try {
      await onFolderRename(folder.id, newName.trim());
      setShowRenameModal(false);
      setNewName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to rename folder');
    }
  };

  const handleConfirmCreateSubfolder = async () => {
    if (!newName || !newName.trim()) {
      setError('Folder name is required');
      return;
    }

    try {
      await onFolderCreate(newName.trim(), folder.id);
      setShowCreateSubfolderModal(false);
      setNewName('');
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to create subfolder');
    }
  };

  const handleCreateProject = () => {
    if (onProjectCreate) {
      onProjectCreate(folder.id);
    }
    closeContextMenu();
  };

  const paddingLeft = `${level * 16 + 8}px`;
  const hasChildren = (folder.children && folder.children.length > 0) || (folder.projects && folder.projects.length > 0);
  const isSelected = currentFolderId === folder.id;

  const handleFolderClick = (e) => {
    // Don't trigger folder selection when clicking on expand/collapse button
    if (e.target.closest('button')) return;
    
    // Call onFolderSelect if provided
    if (onFolderSelect) {
      onFolderSelect(folder);
    }
  };

  return (
    <>
      <div>
        <div
          className={`flex items-center px-3 py-2 cursor-pointer group relative ${
            isSelected 
              ? 'bg-orange-50 border-l-4 border-tqa-accent' 
              : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft }}
          onContextMenu={handleContextMenu}
          onClick={handleFolderClick}
        >
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="mr-2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {hasChildren && (
              <svg
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            )}
            {!hasChildren && <div className="w-4 h-4" />}
          </button>
          
          <svg className="w-4 h-4 mr-2 text-tqa-accent" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
          </svg>
          
          <span className="text-sm font-medium text-gray-700">{folder.name}</span>
        </div>

        {isExpanded && (
          <div>
            {/* Child folders */}
            {folder.children && folder.children.length > 0 && (
              <div>
                {folder.children.map(childFolder => (
                  <FolderNode
                    key={childFolder.id}
                    folder={childFolder}
                    currentProjectId={currentProjectId}
                    currentFolderId={currentFolderId}
                    onProjectSelect={onProjectSelect}
                    onFolderSelect={onFolderSelect}
                    onFolderCreate={onFolderCreate}
                    onFolderRename={onFolderRename}
                    onFolderDelete={onFolderDelete}
                    onFolderMove={onFolderMove}
                    onProjectMove={onProjectMove}
                    onProjectCreate={onProjectCreate}
                    level={level + 1}
                  />
                ))}
              </div>
            )}
            
            {/* Projects in this folder */}
            {folder.projects && folder.projects.length > 0 && (
              <div>
                {folder.projects.map(project => (
                  <ProjectNode
                    key={project.id}
                    project={project}
                    isActive={project.id === currentProjectId}
                    onSelect={onProjectSelect}
                    level={level + 1}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
            style={{ left: `${contextMenuPos.x}px`, top: `${contextMenuPos.y}px` }}
          >
            <button
              onClick={handleCreateSubfolder}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              New Subfolder
            </button>
            <button
              onClick={handleCreateProject}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              New Project
            </button>
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={handleRename}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Rename
            </button>
            <button
              onClick={handleDelete}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Folder</h3>
            {folder.isPersonal && !folder.parentId && folder.name === 'Personal Workspace' ? (
              <>
                <p className="text-sm text-red-600 mb-6">
                  The Personal Workspace folder cannot be deleted.
                </p>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-700 mb-6">
                  Are you sure you want to delete the folder <strong>"{folder.name}"</strong> and all its contents?
                  <br /><br />
                  This action cannot be undone.
                </p>
                {error && <p className="text-red-600 text-sm mb-4">{error}</p>}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelDelete}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rename Folder</h3>
            <div className="mb-4">
              <label htmlFor="rename-folder-input" className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                id="rename-folder-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmRename();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter folder name"
                autoFocus
              />
              {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowRenameModal(false);
                  setNewName('');
                  setError('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                disabled={!newName || !newName.trim() || newName.trim() === folder.name}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Subfolder Modal */}
      {showCreateSubfolderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create Subfolder</h3>
            <p className="text-sm text-gray-600 mb-4">
              Creating subfolder in <strong>"{folder.name}"</strong>
            </p>
            <div className="mb-4">
              <label htmlFor="create-subfolder-input" className="block text-sm font-medium text-gray-700 mb-2">
                Folder Name
              </label>
              <input
                type="text"
                id="create-subfolder-input"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateSubfolder();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter folder name"
                autoFocus
              />
              {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowCreateSubfolderModal(false);
                  setNewName('');
                  setError('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCreateSubfolder}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                disabled={!newName || !newName.trim()}
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ProjectNode = ({ project, isActive, onSelect, level = 0 }) => {
  const { deleteProject, folders, moveProject } = useProject();
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPos, setContextMenuPos] = useState({ x: 0, y: 0 });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newProjectName, setNewProjectName] = useState(project.name);
  const [selectedFolderId, setSelectedFolderId] = useState(project.folderId || null);
  const paddingLeft = `${level * 16 + 32}px`; // Extra indent for projects under folders

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenuPos({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  };

  const closeContextMenu = () => {
    setShowContextMenu(false);
  };

  const handleMoveClick = () => {
    closeContextMenu();
    setShowMoveModal(true);
  };

  const handleRenameClick = () => {
    closeContextMenu();
    setNewProjectName(project.name);
    setShowRenameModal(true);
  };

  const handleDeleteClick = () => {
    closeContextMenu();
    setShowDeleteModal(true);
  };

  const handleConfirmMove = async () => {
    try {
      await moveProject(project.id, selectedFolderId);
      setShowMoveModal(false);
      setSelectedFolderId(project.folderId || null);
    } catch (err) {
      console.error('Error moving project:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to move project';
      alert(errorMessage);
    }
  };

  const handleCancelMove = () => {
    setShowMoveModal(false);
    setSelectedFolderId(project.folderId || null);
  };

  const handleConfirmRename = async () => {
    try {
      if (!newProjectName || !newProjectName.trim() || newProjectName.trim() === project.name) {
        setShowRenameModal(false);
        return;
      }

      const { projectService } = await import('../services/api');
      await projectService.updateProject(project.id, { name: newProjectName.trim() });
      setShowRenameModal(false);
      // Reload to reflect changes
      window.location.reload();
    } catch (err) {
      alert(err.message || 'Failed to rename project');
    }
  };

  const handleCancelRename = () => {
    setShowRenameModal(false);
    setNewProjectName(project.name);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteProject(project.id);
      setShowDeleteModal(false);
    } catch (err) {
      alert(err.message || 'Failed to delete project');
      setShowDeleteModal(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
  };

  // Helper function to recursively render folder options in select dropdown
  const renderFolderOptions = (folderList, depth = 0) => {
    const options = [];
    const indent = '\u00A0\u00A0'.repeat(depth); // Non-breaking spaces for indentation

    folderList.forEach(folder => {
      options.push(
        <option key={folder.id} value={folder.id}>
          {indent}{depth > 0 ? '└ ' : ''}{folder.name}
        </option>
      );

      // Recursively add child folders
      if (folder.children && folder.children.length > 0) {
        options.push(...renderFolderOptions(folder.children, depth + 1));
      }
    });

    return options;
  };

  return (
    <>
      <button
        onClick={() => onSelect(project.id)}
        onContextMenu={handleContextMenu}
        className={`w-full text-left flex items-center px-3 py-2 text-sm transition-colors ${
          isActive
            ? 'bg-blue-100 text-blue-700 font-medium'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
        style={{ paddingLeft }}
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <span className="truncate">{project.name}</span>
      </button>

      {/* Context Menu */}
      {showContextMenu && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={closeContextMenu}
          />
          <div
            className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[160px]"
            style={{ left: `${contextMenuPos.x}px`, top: `${contextMenuPos.y}px` }}
          >
            <button
              onClick={handleRenameClick}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Rename
            </button>
            <button
              onClick={handleMoveClick}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              Move to Folder
            </button>
            <div className="border-t border-gray-200 my-1" />
            <button
              onClick={handleDeleteClick}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
            >
              Delete
            </button>
          </div>
        </>
      )}

      {/* Rename Project Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Rename Project</h3>
            <div className="mb-4">
              <label htmlFor="rename-project-input" className="block text-sm font-medium text-gray-700 mb-2">
                Project Name
              </label>
              <input
                type="text"
                id="rename-project-input"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmRename();
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter project name"
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelRename}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRename}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                disabled={!newProjectName || !newProjectName.trim() || newProjectName.trim() === project.name}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Project Modal */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Move Project</h3>
            <p className="text-sm text-gray-700 mb-4">
              Move <strong>"{project.name}"</strong> to a different folder:
            </p>
            <div className="mb-6">
              <label htmlFor="move-folder" className="block text-sm font-medium text-gray-700 mb-2">
                Select Folder
              </label>
              <select
                id="move-folder"
                value={selectedFolderId || ''}
                onChange={(e) => setSelectedFolderId(e.target.value || null)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select a folder</option>
                {folders.shared && folders.shared.length > 0 && (
                  <optgroup label="Shared Folders">
                    {renderFolderOptions(folders.shared, 0)}
                  </optgroup>
                )}
                {folders.personal && folders.personal.length > 0 && (
                  <optgroup label="Personal Workspace">
                    {renderFolderOptions(folders.personal, 0)}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelMove}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmMove}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                disabled={!selectedFolderId}
              >
                Move Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Project Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Project</h3>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure you want to delete the project <strong>"{project.name}"</strong>?
              <br /><br />
              This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancelDelete}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FolderTree;
